import { myContainer } from "@/inversify.config";
import { TYPES } from "@/types";
import { IBillingService, ContainerBilling } from "@/interfaces/IBillingService";

const billingService = myContainer.get<IBillingService>(TYPES.IBillingService);

interface BillingState {
  containerBilling: ContainerBilling | null;
  loading: boolean;
  error: string | null;
}

const state: BillingState = {
  containerBilling: null,
  loading: false,
  error: null,
};

const getters = {
  billingType: (state: BillingState) => state.containerBilling?.billingType || 'monthly',
  pointsBalance: (state: BillingState) => state.containerBilling?.pointsBalance || 0,
  isPointsBased: (state: BillingState) => state.containerBilling?.billingType === 'points',
  hasEnoughPoints: (state: BillingState) => (requiredPoints: number) => {
    if (state.containerBilling?.billingType === 'monthly') return true;
    return (state.containerBilling?.pointsBalance || 0) >= requiredPoints;
  },
};

const mutations = {
  SET_BILLING(state: BillingState, billing: ContainerBilling) {
    state.containerBilling = billing;
  },
  SET_LOADING(state: BillingState, loading: boolean) {
    state.loading = loading;
  },
  SET_ERROR(state: BillingState, error: string | null) {
    state.error = error;
  },
};

const actions = {
  async fetchContainerBilling({ commit, rootState }: any) {
    const containerId = rootState.containerStore.container?.id;
    if (!containerId) return;

    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      const billing = await billingService.getContainerBilling(containerId);
      commit('SET_BILLING', billing);
    } catch (error: any) {
      commit('SET_ERROR', error.message || 'Failed to fetch billing information');
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async setBillingType({ commit, rootState }: any, billingType: 'monthly' | 'points') {
    const containerId = rootState.containerStore.container?.id;
    if (!containerId) return;

    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      const billing = await billingService.setBillingType(containerId, billingType);
      commit('SET_BILLING', billing);
      return billing;
    } catch (error: any) {
      commit('SET_ERROR', error.message || 'Failed to update billing type');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async addPoints({ commit, rootState }: any, { points, description }: { points: number; description: string }) {
    const containerId = rootState.containerStore.container?.id;
    if (!containerId) return;

    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      const billing = await billingService.addPoints(containerId, points, description);
      commit('SET_BILLING', billing);
      return billing;
    } catch (error: any) {
      commit('SET_ERROR', error.message || 'Failed to add points');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async addPointsToContainer({ commit }: any, { containerId, points, description }: { containerId: number; points: number; description: string }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      const billing = await billingService.addPoints(containerId, points, description);
      // Don't update the current container's billing if it's a different container
      return billing;
    } catch (error: any) {
      commit('SET_ERROR', error.message || 'Failed to add points');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async setBillingTypeForContainer({ commit }: any, { containerId, billingType }: { containerId: number; billingType: 'monthly' | 'points' }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      const billing = await billingService.setBillingType(containerId, billingType);
      // Don't update the current container's billing if it's a different container
      return billing;
    } catch (error: any) {
      commit('SET_ERROR', error.message || 'Failed to update billing type');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};